/**
 * Pure helpers for splitting an AnalyserNode-style frequency byte spectrum
 * (Uint8Array of length fftSize/2, each value 0..255 representing dB-mapped
 * magnitude) into bass / mid / treble bands.
 *
 * This module is sample-rate aware: cutoffs are specified in Hz, not as
 * fractions of bin count. A previous version used fixed bin-count fractions
 * (`bassEnd = floor(n * 0.06)`), which at fftSize=2048, sr=48 kHz pushed the
 * "bass" cutoff to ~1.43 kHz instead of the intended ~250 Hz. That made
 * bass over-detect on vocal/midrange content and starved mid/treble of
 * energy.
 *
 * Cutoffs follow the conventional split used by visualizers and EQs:
 *   bass:   sub..BASS_MAX_HZ        (default 250 Hz)
 *   mid:    BASS_MAX_HZ..MID_MAX_HZ (default 250..4000 Hz)
 *   treble: MID_MAX_HZ..Nyquist     (default 4000+ Hz)
 *
 * All math is kept in this file so it can be unit-tested without spinning up
 * an AudioContext.
 */

export const DEFAULT_BAND_CUTOFFS_HZ = {
  bassMaxHz: 250,
  midMaxHz: 4000,
} as const;

export type BandCutoffsHz = {
  bassMaxHz: number;
  midMaxHz: number;
};

export type BandIndices = {
  /** Inclusive start bin (always 0; we count from DC for completeness). */
  bassStart: number;
  /** Exclusive end bin for bass / inclusive start bin for mid. */
  bassEnd: number;
  /** Exclusive end bin for mid / inclusive start bin for treble. */
  midEnd: number;
  /** Exclusive end bin for treble (== binCount). */
  trebleEnd: number;
};

export type Bands = {
  bass: number;
  mid: number;
  treble: number;
};

/**
 * Convert Hz cutoffs into bin indices for a spectrum of length `binCount`
 * produced from an FFT of size `binCount * 2` at the given sample rate.
 *
 * Bin `i` corresponds to frequency `i * sampleRate / fftSize`
 *                                  = `i * sampleRate / (2 * binCount)`.
 * So for a Hz cutoff `f`, the bin index is `f * 2 * binCount / sampleRate`.
 *
 * The result is clamped so each band has at least one bin and they don't
 * overlap, which matters for very small FFT sizes or unusually low sample
 * rates that would otherwise collapse a band to zero width.
 */
export function bandIndicesFromHz(
  binCount: number,
  sampleRate: number,
  cutoffs: BandCutoffsHz = DEFAULT_BAND_CUTOFFS_HZ,
): BandIndices {
  if (binCount <= 0) {
    return { bassStart: 0, bassEnd: 0, midEnd: 0, trebleEnd: 0 };
  }
  const fftSize = binCount * 2;
  const hzToBin = (f: number): number =>
    Math.round((f * fftSize) / sampleRate);

  // Bass starts at bin 0 (DC). Including bin 0 mirrors how AnalyserNode
  // exposes data and is typically near zero anyway after windowing.
  const bassStart = 0;
  let bassEnd = clampInt(hzToBin(cutoffs.bassMaxHz), 1, binCount - 2);
  let midEnd = clampInt(hzToBin(cutoffs.midMaxHz), bassEnd + 1, binCount - 1);
  const trebleEnd = binCount;

  // Tiny FFT / odd sample rate fallback: ensure bass and mid each have
  // at least one bin.
  if (bassEnd <= bassStart) bassEnd = bassStart + 1;
  if (midEnd <= bassEnd) midEnd = bassEnd + 1;

  return { bassStart, bassEnd, midEnd, trebleEnd };
}

/**
 * Average the byte values of `buf` over `[start, end)` and normalize to 0..1.
 * Returns 0 for an empty range.
 */
export function avgRangeNormalized(
  buf: Uint8Array,
  start: number,
  end: number,
): number {
  const a = Math.max(0, start);
  const b = Math.min(buf.length, end);
  if (b <= a) return 0;
  let sum = 0;
  for (let i = a; i < b; i++) sum += buf[i] ?? 0;
  return sum / (b - a) / 255;
}

/**
 * Compute bass / mid / treble levels from a frequency byte spectrum
 * (`AnalyserNode#getByteFrequencyData` style: Uint8Array of length fftSize/2,
 * each entry 0..255 mapped from `[minDecibels, maxDecibels]`).
 *
 * Output values are in 0..1 and are RAW (no temporal smoothing). The caller
 * is responsible for any smoothing / envelope shaping.
 */
export function extractBands(
  fdData: Uint8Array,
  sampleRate: number,
  cutoffs: BandCutoffsHz = DEFAULT_BAND_CUTOFFS_HZ,
): Bands {
  const idx = bandIndicesFromHz(fdData.length, sampleRate, cutoffs);
  return {
    bass: avgRangeNormalized(fdData, idx.bassStart, idx.bassEnd),
    mid: avgRangeNormalized(fdData, idx.bassEnd, idx.midEnd),
    treble: avgRangeNormalized(fdData, idx.midEnd, idx.trebleEnd),
  };
}

function clampInt(v: number, lo: number, hi: number): number {
  if (hi < lo) return lo;
  return Math.min(hi, Math.max(lo, v));
}
